import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { HttpService } from 'src/app/services/http.service';
import { LanguageService } from 'src/app/services/language.service';
import { UtilsService } from 'src/app/services/utils.service';
import { ConfirmationDialogData } from '../../modals/confirmation-dialog-data';
import { ConfirmationDialogComponent } from '../../modals/confirmation-dialog/confirmation-dialog.component';
import { ErrorDialogData } from '../../modals/error-dialog-data';
import { ErrorDialogComponent } from '../../modals/error-dialog/error-dialog.component';
import { ReleaseOverview } from '../releaseOverview';
import { forkJoin } from 'rxjs';
import { BikeComponent } from '../../component/component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { AccountService } from 'src/app/services/account.service';

/** List view of the releases */

@Component({
  selector: 'app-release-overview',
  templateUrl: './release-overview.component.html',
  styleUrls: ['./release-overview.component.scss'],
  standalone: false
})
export class ReleaseOverviewComponent implements OnInit {

  public loading: boolean = true;
  public componentClassOptionsList: { id: number, name: string }[] = [];
  public componentList: BikeComponent[] = [];
  public oeOptionsList: { id: number, name: string }[] = [];
  public dataError: boolean = false;
  public rolloutUsedByOptions: { id: string, value: string, startValue: string }[] = [];
  private dropdownList: any = {};

  public releaseList: {
    componentClass: { id: number, name: string },
    components: {
      component: { id: number, name: string },
      componentVariants: {
        variant: { id: number, name: string, OEMName: string },
        releases: ReleaseOverview[],
        configurations: number[]
      }[]
    }[]
  }[] = [];

  constructor(public languageService: LanguageService, private httpService: HttpService,
    private router: Router, private matDialog: MatDialog, private utilsService: UtilsService,
    private snackBar: MatSnackBar, private accountService: AccountService) { }

  /** Initializing release */
  ngOnInit(): void {
    this.rolloutUsedByOptions = this.utilsService.getRolloutUsedByOptions();
    try {
      const dropdownData = localStorage.getItem("dropdown");
      if (dropdownData) {
        this.dropdownList = JSON.parse(dropdownData);
      }
    } catch (e) { }
    this.initReleases();
  }

  /** Getting releases from Api endpoint */
  initReleases() {
    this.loading = true;
    forkJoin([
      this.httpService.getComponentClassOptionsList(),
      this.httpService.getComponentList(),
      this.httpService.getReleaseList(),
      this.httpService.getOEOptionsList(1, 0)
    ]).subscribe((resp) => {
      this.oeOptionsList = resp[3];

      if (resp[0]?.length == 0 || (resp[1] as any)?.success != true || (resp[2] as any)?.success != true) {
        this.dataError = true;
        this.loading = false;
        return;
      }

      this.releaseList = resp[0].map((el) => { return { componentClass: { ...el }, components: [] } });
      this.componentClassOptionsList = resp[0];

      (resp[1] as any).data?.forEach((componentEl: any) => {

        const componentObj: {
          component: { id: number, name: string },
          componentVariants: {
            variant: { id: number, name: string, OEMName: string },
            releases: ReleaseOverview[],
            configurations: number[]
          }[]
        } = {
          component: { id: componentEl.id, name: componentEl.component_name },
          componentVariants: []
        };

        componentEl.variants.forEach((componentVariantEl: any) => {
          const configurationsArray: number[] = [];

          let approvedCounter = 0;
          const releases: ReleaseOverview[] = (resp[2] as any).data?.filter((releaseEl: any) => {
            return releaseEl.component_variant?.id == componentVariantEl.id
          }).reverse().map((releaseEl: any) => {

            releaseEl.component_variant?.configurations?.forEach((configurationEl: any) => {
              if (!configurationsArray.includes(configurationEl.id)) {
                configurationsArray.push(configurationEl.id);
              }
            });

            if (releaseEl.approval_status == 3) {
              approvedCounter++;
            }

            return {
              Id: releaseEl.id,
              ReleaseVersion: releaseEl.release_version,
              FileName: releaseEl.filename,
              ComponentId: releaseEl.component_variant.component.id,
              ComponentVariantId: releaseEl.component_variant.id,
              ReleaseNotes: releaseEl.release_notes,
              ActivationStatus: releaseEl.activation_status ? releaseEl.activation_status : 1,
              ApprovalStatus: releaseEl.approval_status ? releaseEl.approval_status : 1,
              RolloutStatus: releaseEl.rollout_status ? releaseEl.rollout_status : 1,
              RolloutUsedBy: this.rolloutUsedByOptions.find((el) => { return el.id == releaseEl.rollouts[0]?.used_by })?.value,
              InactiveButton: approvedCounter > 1 ? true : false,
              ModifyRole: this.accountService.hasSoftwareModifyRole() && this.accountService.getOEMs().includes(componentVariantEl.OEM_ID),
              ReleaseDeleteInactive: releaseEl.downloads > 0
            }
          });

          const componentVariantObj = {
            variant: {
              id: componentVariantEl.id, name: componentVariantEl.variant_name,
              OEMName: this.utilsService.getOEMName(componentVariantEl.OEM_ID, this.oeOptionsList)
            },
            releases: releases.reverse(),
            configurations: configurationsArray.sort((a, b) => a - b)
          };

          componentObj.componentVariants.push(componentVariantObj);
        });

        this.releaseList.find((el) => { return el.componentClass.id == componentEl.component_class })?.components.push(componentObj);
      });

      this.loading = false;
    });
  }

  /** Opening a release details view */
  viewRelease(releaseId: number) {
    this.router.navigate(['/view-software'], { queryParams: { id: releaseId } });
  }

  viewMonitorRelease(releaseId: number) {
    this.router.navigate(['/monitor-software'], { queryParams: { id: releaseId } });
  }

  viewConfiguration(configurationId: number) {
    this.router.navigate(['/view-configuration'], { queryParams: { id: configurationId } });
  }

  viewAllConfiguration() {
    this.router.navigate(['/configurations']);
  }

  openStartApproval(releaseId: number) {
    this.router.navigate(['/start-approval-rollout'], { queryParams: { id: releaseId } });
  }

  openStartRollout(releaseId: number, inactiveButton: boolean) {
    if (inactiveButton) {
      return;
    }
    this.router.navigate(['/start-rollout'], { queryParams: { id: releaseId } });
  }

  startRollout(releaseElement: ReleaseOverview, rolloutType: string): void {

    let message = "";
    switch (rolloutType) {
      case ("eol"):
        message = this.languageService.languageJSON.ReleaseOverview_StartEol_Desc;
        break;
      case ("mobile"):
        message = this.languageService.languageJSON.ReleaseOverview_StartPublic_Desc;
        break;
      case ("all"):
        message = this.languageService.languageJSON.ReleaseOverview_StartBoth_Desc;
        break;
    }

    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.Global_Activate,
      message: message.replace("$filename", `<b>${releaseElement.FileName}</b>`),
      buttons: [this.languageService.languageJSON.Global_Start, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.loading;
        this.httpService.startRollout(releaseElement.Id, rolloutType).subscribe((resp) => {
          this.loading = false;
          this.initReleases();
        }, () => { this.loading = false; });
      }
    });
  }

  forceApproval(release: ReleaseOverview): void {
    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.ReleaseOverview_ForceApproval,
      message: this.languageService.languageJSON.ReleaseOverview_ForceApproveMessage.replace("$software", `<b>${release.FileName}</b>`),
      buttons: [this.languageService.languageJSON.Global_Approve, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.loading = true;
        this.httpService.changeReleaseStatus(release.Id.toString(), release.ComponentVariantId, release.ReleaseVersion, 1, 3, 3).subscribe((resp: any) => {
          if (resp?.success) {
            this.snackBar.open(this.languageService.languageJSON.ReleaseOverview_ForceApprovalSuccess.replace("$filename", release.FileName), undefined, { panelClass: 'success-snackbar', duration: 8000 });
            this.initReleases();
          }
          else {
            this.snackBar.open(this.languageService.languageJSON.Global_ServerError_Snackbar, undefined, { panelClass: 'success-snackbar', duration: 5000 });
            this.loading = false;
          }
        });
      }
    });
  }

  rejectRollout(release: ReleaseOverview): void {
    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.Global_Reject,
      message: this.languageService.languageJSON.ReleaseOverview_RejectMessage.replace("$software", `<b>${release.FileName}</b>`),
      buttons: [this.languageService.languageJSON.Global_Reject, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.loading = true;
        this.httpService.changeReleaseStatus(release.Id.toString(), release.ComponentVariantId, release.ReleaseVersion,
          1, 1, 3).subscribe((resp: any) => {
            if (resp?.success) {
              this.snackBar.open(this.languageService.languageJSON.ReleaseOverview_RejectRolloutSuccess.replace("$filename", release.FileName), undefined, { panelClass: 'success-snackbar', duration: 8000 });
              this.initReleases();
            }
            else {
              this.snackBar.open(this.languageService.languageJSON.Global_ServerError_Snackbar, undefined, { panelClass: 'success-snackbar', duration: 5000 });
              this.loading = false;
            }
          });
      }
    });
  }

  approveRollout(release: ReleaseOverview): void {
    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.Global_Approve,
      message: this.languageService.languageJSON.ReleaseOverview_ApproveMessage.replace("$software", `<b>${release.FileName}</b>`),
      buttons: [this.languageService.languageJSON.Global_Approve, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.loading = true;
        this.httpService.changeReleaseStatus(release.Id.toString(), release.ComponentVariantId, release.ReleaseVersion,
          1, 3, 3).subscribe((resp: any) => {
            if (resp?.success) {
              this.snackBar.open(this.languageService.languageJSON.ReleaseOverview_ApproveRolloutSuccess.replace("$filename", release.FileName), undefined, { panelClass: 'success-snackbar', duration: 8000 });
              this.initReleases();
            }
            else {
              this.snackBar.open(this.languageService.languageJSON.Global_ServerError_Snackbar, undefined, { panelClass: 'success-snackbar', duration: 5000 });
              this.loading = false;
            }
          });
      }
    });
  }

  deactivateRollout(release: ReleaseOverview): void {
    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.ReleaseOverview_Deactivate,
      message: this.languageService.languageJSON.ReleaseOverview_DeactivateMessage.replace("$software", `<b>${release.FileName}</b>`),
      buttons: [this.languageService.languageJSON.ReleaseOverview_Deactivate, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.loading = true;
        this.httpService.changeReleaseStatus(release.Id.toString(), release.ComponentVariantId, release.ReleaseVersion,
          1, 3, 3).subscribe((resp: any) => {
            if (resp?.success) {
              this.snackBar.open(this.languageService.languageJSON.ReleaseOverview_DeactivateRolloutSuccess.replace("$filename", release.FileName), undefined, { panelClass: 'success-snackbar', duration: 5000 });
              this.initReleases();
            }
            else {
              this.snackBar.open(this.languageService.languageJSON.Global_ServerError_Snackbar, undefined, { panelClass: 'success-snackbar', duration: 5000 });
              this.loading = false;
            }
          });
      }
    });
  }

  getActivationStatus(statusValue: number): string {
    return this.utilsService.getActivationState(statusValue);
  }

  getApprovalStatus(statusValue: number): string {
    return this.utilsService.getApprovalState(statusValue);
  }

  getRolloutStatus(statusValue: number): string {
    return this.utilsService.getRolloutState(statusValue);
  }

  onDropdownClick(dropdownId: string): void {
    if (this.dropdownList[dropdownId] != undefined) {
      this.dropdownList[dropdownId] = !this.dropdownList[dropdownId];
    }
    else {
      this.dropdownList[dropdownId] = true;
    }

    localStorage.setItem("dropdown", JSON.stringify(this.dropdownList));
  }

  public getDropdown(dropdownId: string): boolean {
    return (this.dropdownList && this.dropdownList[dropdownId] == true);
  }

  /** Calling delete release Api endpoint */
  deleteRelease(release: ReleaseOverview): void {
    if (release.ReleaseDeleteInactive) {
      return;
    }

    const data: ConfirmationDialogData = {
      title: this.languageService.languageJSON.ReleaseOverview_DeleteConfirmation_Title,
      message: this.languageService.languageJSON.Global_Delete_MessageWithName.replace("$name", `<b>${release.FileName}</b>`),
      buttons: [this.languageService.languageJSON.Global_Delete, this.languageService.languageJSON.Global_Cancel]
    };

    this.matDialog.open(ConfirmationDialogComponent, { data: { data } }).afterClosed().subscribe((result) => {
      if (result) {
        this.httpService.deleteRelease(release.Id as number).subscribe((resp: any) => {
          if (resp?.success) {
            this.snackBar.open(this.languageService.languageJSON.ReleaseDetails_DeleteMessage.replace("$name", `${release.FileName}`), undefined, { panelClass: 'success-snackbar', duration: 5000 });
            this.initReleases();
          }
          else if (resp.code == "E013") {
            let errorMsg = this.languageService.languageJSON.ReleaseDetails_DeleteFailedMessage.replace("$name", `${release.FileName}`);
            errorMsg += `. ${this.languageService.languageJSON.ReleaseDetails_DeleteFailedMessage_E013}`;

            this.snackBar.open(errorMsg, undefined, { panelClass: 'success-snackbar', duration: 5000 });
            this.initReleases();
          }
          else if (resp?.success == false) {
            const data: ErrorDialogData = {
              title: this.languageService.languageJSON.ReleaseOverview_DeleteError_Title,
              message: this.languageService.languageJSON.ReleaseOverview_DeleteError_Desc.replace("$name", `<b>${release.FileName}</b>`) +
                this.languageService.languageJSON['ReleaseDetails_ErrorCode_' + resp.code]
            }
            this.matDialog.open(ErrorDialogComponent, { data: { data } });
          }
        });
      }
    });
  }
}
